import "reactflow/dist/style.css";

import ReactFlow, { ReactFlowProvider, applyNodeChanges } from "reactflow";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CardFactory from "../factories/CardFactory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { deleteCardById } from "../../api/api";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useWSOutExecuteRequest } from "../../components/hooks/useWebSocket";

function PipelineDrawer({
  token,
  cards,
  topics,
  pipelineLayouts,
  updateLayouts,
  onClose,
}) {
  return (
    <>
      <div className="fixed top-0 right-0 h-full w-4/5 bg-white shadow-lg z-50 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-2 p-4 border-b">
          <h2 className="text-2xl font-bold">Pipeline Builder</h2>
          <button onClick={onClose}>
            <FontAwesomeIcon
              icon={faXmark}
              className="text-gray-500 hover:text-black text-2xl"
            />
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          <PipelineBuilder
            token={token}
            cards={cards}
            topics={topics}
            pipelineLayouts={pipelineLayouts}
            updateLayouts={updateLayouts}
          />
        </div>
      </div>
      <div className="fixed inset-0 bg-black bg-opacity-30" onClick={onClose} />
    </>
  );
}

const PipelineBuilder = ({
  token,
  cards,
  topics,
  pipelineLayouts,
  updateLayouts,
}) => {
  const executeRequest = useWSOutExecuteRequest(token);

  const [triggerCards, setTriggerCards] = useState([]);
  const [actionCards, setActionCards] = useState([]);

  useEffect(() => {
    if (cards && cards.length > 0) {
      const triggers = cards.filter((card) => card.type === "trigger");
      const actions = cards.filter((card) => card.type === "action");
      setTriggerCards(triggers);
      setActionCards(actions);
    }
  }, [cards]);

  const getTopicById = (id) => topics.find((topic) => topic.id === id);

  // Build reverse dependency graph to find root triggers and build tree
  const actionsById = Object.fromEntries(
    actionCards.map((a) => [a.topics[0]?.name, a])
  );

  // --- TREE CACHE AND BUILD LOGIC SUPPORTING GROUPED TRIGGERS/ACTIONS ---
  // The cache key is a stringified group of trigger ids and action ids to allow grouping.
  const treeCache = new Map();

  function mergeCards(existingArr, newCard) {
    // Simple deduplication by id
    if (!existingArr.some((c) => c.id === newCard.id)) {
      existingArr.push(newCard);
    }
    return existingArr;
  }

  // Group nodes by their children/action relationships
  const buildTree = (triggerCard, visited = new Set()) => {
    if (visited.has(triggerCard.id)) return null;
    visited.add(triggerCard.id);

    const topicName = triggerCard.params?.action;
    const actionCard = actionsById[topicName];

    const childrenTriggers = triggerCards.filter(
      (t) => t.params?.topic === actionCard?.topics[0]?.name
    );

    const triggerIds = [triggerCard.id];
    const actionIds = actionCard ? [actionCard.id] : [];
    const cacheKey = JSON.stringify({
      t: triggerIds.sort(),
      a: actionIds.sort(),
    });

    if (treeCache.has(cacheKey)) {
      const cached = treeCache.get(cacheKey);
      mergeCards(cached.trigger, triggerCard);
      if (actionCard) mergeCards(cached.action, actionCard);
      return cached;
    }

    const subtree = {
      trigger: [triggerCard],
      action: actionCard ? [actionCard] : [],
      children: childrenTriggers
        .map((child) => buildTree(child, new Set(visited)))
        .filter(Boolean),
    };
    treeCache.set(cacheKey, subtree);
    return subtree;
  };

  const buildMergedTrees = () => {
    const allTriggersUsed = new Set();

    triggerCards.forEach((t) => {
      const topicName = t.params?.topic;
      if (topicName) {
        triggerCards.forEach((parent) => {
          if (parent.params?.action === topicName) {
            allTriggersUsed.add(t.id);
          }
        });
      }
    });

    const roots = triggerCards.filter((t) => !allTriggersUsed.has(t.id));
    return [...new Set(roots.map((r) => buildTree(r, new Set())))];
  };

  // --- FLATTEN TREE TO NODES AND EDGES FOR REACT FLOW ---
  const triggerTrees = useMemo(
    () => buildMergedTrees(),
    [triggerCards, actionCards]
  );

  // 1. Flatten all cards (triggers and actions) into a flat array (no duplicates)
  const flattenAllCards = () => {
    const seen = new Set();
    const cardsArr = [];
    function walk(node) {
      for (const trig of node.trigger) {
        if (!seen.has(trig.id)) {
          cardsArr.push(trig);
          seen.add(trig.id);
        }
      }
      for (const act of node.action || []) {
        if (!seen.has(act.id)) {
          cardsArr.push(act);
          seen.add(act.id);
        }
      }
      for (const child of node.children || []) {
        walk(child);
      }
    }
    triggerTrees.forEach(walk);
    return cardsArr;
  };

  // 2. Build individual React Flow nodes for each card
  const allCards = useMemo(() => flattenAllCards(), [triggerTrees]);
  // Assign x/y positions for layout; for now, stack vertically
  const nodeVerticalSpacing = 120;
  const nodeHorizontalSpacing = 300;

  const [rfNodes, setRfNodes] = useState([]);

  useEffect(() => {
    // If pipelineLayouts is defined (even an empty array), use it directly
    if (pipelineLayouts !== undefined && pipelineLayouts !== null) {
      const nodes = Array.isArray(pipelineLayouts)
        ? pipelineLayouts.map((layoutNode) => {
            const card = allCards.find(
              (c) => String(c.id) === String(layoutNode.id)
            );
            return {
              id: String(layoutNode.id),
              type: "default",
              position: layoutNode.position,
              data: {
                label: card ? (
                  <CardFactory
                    card={card}
                    topic={getTopicById(card.topics[0]?.id)}
                    w={3}
                    h={2}
                    pipeline
                    executeRequest={executeRequest}
                  />
                ) : null,
              },
              draggable: true,
              selectable: true,
            };
          })
        : [];
      setRfNodes(nodes);
      return;
    }

    // Fallback: compute node positions from allCards only if no pipelineLayouts exist yet
    if (!allCards?.length) {
      setRfNodes([]);
      return;
    }
    const nodes = allCards.map((card, idx) => {
      const pos = {
        x: card.type === "action" ? nodeHorizontalSpacing : 0,
        y: idx * nodeVerticalSpacing,
      };
      return {
        id: String(card.id),
        type: "default",
        position: pos,
        data: {
          label: (
            <CardFactory
              card={card}
              topic={getTopicById(card.topics[0]?.id)}
              w={3}
              h={2}
              pipeline
              executeRequest={executeRequest}
            />
          ),
        },
        draggable: true,
        selectable: true,
      };
    });
    setRfNodes(nodes);
  }, [allCards, pipelineLayouts]);

  const onNodesChange = useCallback(
    (changes) => setRfNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onNodeDragStop = useCallback(() => {
    if (updateLayouts) {
      updateLayouts("pipeline", rfNodes);
    }
  }, [rfNodes, updateLayouts]);

  // Removed the useEffect that triggers updateLayouts on every rfNodes change

  // 3. Build edges: for each trigger→action→trigger relationship, create edges
  // We'll traverse the tree and for each trigger→action, and action→child trigger, create edges
  const edgeMap = new Map();
  function buildEdges(node) {
    for (const trig of node.trigger) {
      for (const act of node.action || []) {
        const id1 = `${trig.id}->${act.id}`;
        if (!edgeMap.has(id1)) {
          edgeMap.set(id1, {
            id: id1,
            source: String(trig.id),
            target: String(act.id),
          });
        }
        for (const child of node.children || []) {
          for (const childTrig of child.trigger) {
            const id2 = `${act.id}->${childTrig.id}`;
            if (!edgeMap.has(id2)) {
              edgeMap.set(id2, {
                id: id2,
                source: String(act.id),
                target: String(childTrig.id),
              });
            }
          }
        }
      }
    }
    for (const child of node.children || []) {
      buildEdges(child);
    }
  }
  triggerTrees.forEach(buildEdges);
  const edges = Array.from(edgeMap.values());

  // 4. Render ReactFlow with nodes/edges
  return (
    <div className="w-full h-full">
      <ReactFlowProvider>
        <ReactFlow
          nodes={rfNodes}
          edges={edges}
          fitView
          panOnDrag
          nodesDraggable={true}
          nodesConnectable={false}
          elementsSelectable={false}
          onNodesChange={onNodesChange}
          onNodeDragStop={onNodeDragStop}
        />
      </ReactFlowProvider>
    </div>
  );
};

export default PipelineDrawer;
